import React, { useState, useEffect } from 'react';
import {
  Container, VStack, Heading, Text, FormControl, Input, Button, Center,
  Flex, Box, HStack, InputGroup, InputRightElement, Select, useBreakpointValue
} from '@chakra-ui/react';
import { colors } from '../../theme/index';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, updateToken } from '../../state';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as LynxSplash } from './lynx_spash.svg';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ErrorAlert from '../../components/ErrorAlert';
import { useColorMode, useColorModeValue } from '@chakra-ui/react';

import ReactFlagsSelect from 'react-flags-select';

const DetailsPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('91');
  const [gender, setGender] = useState('');
  const [countryOfOrigin, setCountryOfOrigin] = useState('');
  const [error, setError] = useState('');
  const { colorMode } = useColorMode();


const inputBgColor = useColorModeValue(colors.inputBgLight, colors.inputBgDark);
const buttonBgColor = useColorModeValue(colors.buttonBgLight, colors.buttonBgDark);


  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector(state => state.auth.token);

  useEffect(() => {
    if (token) {
      dispatch(updateToken(token));
    }
  }, [token, dispatch]);

 const handleContinue = () => {
    if (!phoneNumber || !gender || !countryOfOrigin) {
      setError('Please fill in all the required fields.');
      return;
    }

    if (!token) {
      setError('Token is missing. Cannot proceed with registration.');
      return;
    }

    setError('');

    // Handle phone number splitting
    let user_country_code = '';
    let user_phone_number = '';

    // Check if phone number has a space
    if (phoneNumber.indexOf(' ') !== -1) {
      user_country_code = phoneNumber.slice(0, phoneNumber.indexOf(' '));
      user_phone_number = phoneNumber.slice(phoneNumber.indexOf(' ') + 1);
    } else {
      user_country_code = phoneNumber.slice(0, 2);
      user_phone_number = phoneNumber.slice(2);  
    }

    // Dispatch user registration action
    dispatch(registerUser({
      user_country_code,
      user_phone_number,
      user_gender: gender,
      user_country_of_origin: countryOfOrigin
    }));

    navigate('/register-dob-details');
  };


  const handleBack = () => {
    navigate('/register-otp');
  };

  const splashRender = useBreakpointValue({ base: false, lg: true });

  return (
    <HStack justify={"center"} spacing={0} h={"100vh"}>
      <Container h="100vh" w="100%" mx={0} px={0}>
        <Center h="full">
          <VStack spacing={12} w={{ base: "90%", md: "70%", lg: "50%" }}>
            <VStack spacing={[0.1, 0.5, 1]}>
              <Heading fontWeight={700}>
                Registration
              </Heading>
              <Flex direction="row">
                <Text color={colors.secondary} fontWeight={400} fontSize={"0.85em"}>
                  Basic Information
                </Text>
                <Box width="3px" />
              </Flex>
            </VStack>
            <VStack spacing={4} w="100%">
              {error && (
                <ErrorAlert
                  message={error}
                  onClose={() => setError('')}
                />
              )}
              <FormControl width="100%">
                <ReactPhoneInput
                  placeholder='Enter phone number'
                  defaultCountry='IN'
                  value={phoneNumber}
                  onChange={(value) => setPhoneNumber(value)}
                  inputStyle={{
                    width: '100%',
                    borderRadius: '4px',
                    background: inputBgColor,
                    fontWeight: '600',
                    paddingLeft: '50px',
                  }}
                  buttonStyle={{
                    borderRadius: '4px 0 0 4px',
                    backgroundColor: buttonBgColor,
                  }}
                  containerStyle={{
                    width: '100%',
                    position: 'relative',
                    zIndex: 2,
                  }}
                  dropdownStyle={{
                    zIndex: 10,
                    backgroundColor: inputBgColor === '#EDF2F7' ? '#FFFFFF' : '#2D3748',
                    color: inputBgColor === '#EDF2F7' ? '#000000' : '#FFFFFF',
                  }}
                  dropdownItemStyle={{
                    color: inputBgColor === '#EDF2F7' ? '#000000' : '#B0B0B0',
                    backgroundColor: inputBgColor === '#EDF2F7' ? '#FFFFFF' : '#2D3748',
                    transition: 'background-color 0.2s ease',
                  }}
                  dropdownItemHighlightedStyle={{
                    color: '#000000',
                    backgroundColor: inputBgColor === '#EDF2F7' ? '#D0D0D0' : '#4A5568',
                  }}
                  dropdownItemHighlightedCountryCodeStyle={{
                    color: '#B0B0B0',
                  }}
                />
              </FormControl>

              <FormControl width="100%">
                <InputGroup>
                  <Input
                    placeholder='Sex'
                    _placeholder={{ fontWeight: "600" }}
                    pr="4.5rem"
                    type="text"
                    variant="filled"
                    borderRadius="4px"
                    value={gender.toUpperCase()}
                    readOnly
                  />
                  <InputRightElement width="8rem">
                    <Select
                      placeholder='Select'
                      onChange={(e) => setGender(e.target.value)}
                      size="sm"
                      border="none"
                      borderRadius="4px"
                      _focus={{ boxShadow: 'none' }}
                    >
                      <option value='male'>MALE</option>
                      <option value='female'>FEMALE</option>
                    </Select>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl width="100%">
                <ReactFlagsSelect
                  selected={countryOfOrigin}
                  onSelect={(code) => setCountryOfOrigin(code)}
                  countries={[
                    "IN", "US", "GB", "FR", "DE", "IT", "JP", "CA", "BR", "AU",
                    "CN", "RU", "ES", "MX", "KR", "ZA", "NL", "SE", "NO",
                    "FI", "IE", "CH", "AT", "DK", "PL", "PT", "BE", "CZ",
                    "HU", "SG", "TH"
                  ]}
                  placeholder="Select Country of Origin"
                  searchable
                  customLabels={{
                    IN: "India",
                    US: "United States",
                    GB: "United Kingdom",
                    FR: "France",
                    DE: "Germany",
                    IT: "Italy",
                    JP: "Japan",
                    CA: "Canada",
                    BR: "Brazil",
                    AU: "Australia",
                    CN: "China",
                    RU: "Russia",
                    ES: "Spain",
                    MX: "Mexico",
                    KR: "South Korea",
                    ZA: "South Africa",
                    NL: "Netherlands",
                    SE: "Sweden",
                    NO: "Norway",
                    FI: "Finland",
                    IE: "Ireland",
                    CH: "Switzerland",
                    AT: "Austria",
                    DK: "Denmark",
                    PL: "Poland",
                    PT: "Portugal",
                    BE: "Belgium",
                    CZ: "Czech Republic",
                    HU: "Hungary",
                    SG: "Singapore",
                    TH: "Thailand",
                  }}
                  styles={{
                    select: {
                      backgroundColor: inputBgColor,
                      color: colorMode === 'dark' ? '#FFFFFF' : '#000000',
                      borderRadius: '4px',
                      padding: '10px',
                      width: '100%',
                      fontWeight: '600',
                    },
                    option: {
                      backgroundColor: colorMode === 'dark' ? '#2D3748' : '#FFFFFF',
                      color: colorMode === 'dark' ? '#FFFFFF' : '#000000',
                    },
                    optionHover: {
                      backgroundColor: buttonBgColor,
                      color: '#FFFFFF',
                    },
                    optionSelected: {
                      backgroundColor: colorMode === 'dark' ? '#4A5568' : '#D0D0D0',
                      color: '#FFFFFF',
                    },
                  }}

                />
              </FormControl>

              <Button
                w="full"
                colorScheme="brand"
                borderRadius="4px"
                marginTop="15px"
                onClick={handleContinue}
              >
                Continue
              </Button>
              <Button
                w={"full"}
                variant={"ghost"}
                borderRadius={"4px"}
                onClick={handleBack}
              >
                Back
              </Button>
            </VStack>
          </VStack>
        </Center>
      </Container>
      {splashRender && (
        <Center h={"full"} m={0} p={0}>
          <LynxSplash style={{ height: "90%" }} />
        </Center>
      )}
    </HStack>
  );
};

export default DetailsPage;